import { Vector3, type Web3DViewer } from '@technology/web3d';
import { IconsPlugin, type PointIcon } from '@technology/web3d-plugin-icons';
import { getLogger } from '@trimble/logging';
import { arePointsEqual, type Point3D } from '@trimble/utilities';
import {
  CapabilityCoordinatorBase,
  type CapabilityRegistry,
} from '@trimble/web3d';
import { batch, createEffect, createRoot, createSignal } from 'solid-js';
import { configReady, getMarkerIconPath, iconsVersion } from '../constants';
import { resolveMarkerHashId } from '../utils';
import {
  defaultPaneEligibility,
  getEligibleViewers,
  type PaneEligibilityOptions,
} from './pane-eligibility';

const PREVIEW_MARKER_ICON_ID = -1;
const MARKER_ICON_SIZE = 32;
/** Max distance (metres) to treat a persisted marker as replacing the preview. */
const PREVIEW_MATCH_EPSILON = 1e-3;

/** Structurally compatible with IssueMarkerInfo, without depending on the UI package. */
export interface MarkerInfo {
  readonly issueId: string;
  readonly point: Readonly<Point3D>;
  readonly type?: string;
  readonly priority?: string;
}

export type IssueMarkerIconCoordinatorOptions = PaneEligibilityOptions;

/**
 * Manages issue marker icons across eligible Web3D viewers:
 * a single preview marker during placement and the full list of persisted
 * topic markers.
 */
export class IssueMarkerIconCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  private readonly isPaneEligible: NonNullable<
    IssueMarkerIconCoordinatorOptions['isPaneEligible']
  >;

  private readonly getActiveMarkerPoint: () => Readonly<Point3D> | null;

  private readonly updateActiveMarkerPoint: (
    point: Readonly<Point3D> | null
  ) => void;

  private readonly updatePreviewMarkerVisuals: (
    visuals: { markerType?: string; priority?: string } | null
  ) => void;

  private readonly updateListMarkers: (markers: readonly MarkerInfo[]) => void;

  private readonly updateSelectedMarkerId: (id: string | null) => void;

  private readonly iconOperationChains = new WeakMap<
    Web3DViewer,
    Promise<void>
  >();

  private readonly listIconIds = new WeakMap<Web3DViewer, Set<number>>();

  private previousViewers: Web3DViewer[] = [];
  private previousListViewers: Web3DViewer[] = [];
  private disposeRoot: (() => void) | undefined;

  constructor(
    registry: CapabilityRegistry,
    options: IssueMarkerIconCoordinatorOptions = {}
  ) {
    super(registry);
    this.isPaneEligible = options.isPaneEligible ?? defaultPaneEligibility;

    const [activeMarkerPoint, updateActiveMarkerPoint] =
      createSignal<Readonly<Point3D> | null>(null);
    this.getActiveMarkerPoint = activeMarkerPoint;
    this.updateActiveMarkerPoint = updateActiveMarkerPoint;

    const [previewMarkerVisuals, updatePreviewMarkerVisuals] = createSignal<{
      markerType?: string;
      priority?: string;
    } | null>(null);
    this.updatePreviewMarkerVisuals = updatePreviewMarkerVisuals;

    const [listMarkers, updateListMarkers] = createSignal<
      readonly MarkerInfo[]
    >([]);
    this.updateListMarkers = updateListMarkers;

    const [selectedMarkerId, updateSelectedMarkerId] = createSignal<
      string | null
    >(null);
    this.updateSelectedMarkerId = updateSelectedMarkerId;

    this.disposeRoot = createRoot((dispose) => {
      createEffect(() => {
        const point = activeMarkerPoint();
        const visuals = previewMarkerVisuals();
        const currentViewers = this.getEligibleViewers();

        const staleViewers = this.previousViewers.filter(
          (viewer) => !currentViewers.includes(viewer)
        );
        if (staleViewers.length > 0) {
          this.applyPreviewMarkerToViewers(null, null, staleViewers);
        }
        this.applyPreviewMarkerToViewers(point, visuals, currentViewers);
        this.previousViewers = currentViewers;
      });

      createEffect(() => {
        if (!configReady()) return;
        void iconsVersion();

        const markers = listMarkers();
        const selectedId = selectedMarkerId();
        const currentViewers = this.getEligibleViewers();

        const staleViewers = this.previousListViewers.filter(
          (viewer) => !currentViewers.includes(viewer)
        );
        if (staleViewers.length > 0) {
          this.applyMarkersListToViewers([], null, staleViewers);
        }
        this.applyMarkersListToViewers(markers, selectedId, currentViewers);
        this.previousListViewers = currentViewers;
      });

      return dispose;
    });
  }

  public showMarker(
    point: Readonly<Point3D>,
    visuals?: { markerType?: string; priority?: string }
  ): void {
    this.updatePreviewMarkerVisuals(visuals ?? null);
    this.updateActiveMarkerPoint(point);
  }

  public removeMarker(): void {
    this.updateActiveMarkerPoint(null);
    this.updatePreviewMarkerVisuals(null);
  }

  /**
   * @intent Clear active preview when persisted marker reaches the same point,
   * then update list markers so preview and persisted icons do not coexist.
   */
  public setMarkers(markers: readonly MarkerInfo[]): void {
    batch(() => {
      const preview = this.getActiveMarkerPoint();
      if (
        preview &&
        markers.some((marker) =>
          arePointsEqual(marker.point, preview, PREVIEW_MATCH_EPSILON)
        )
      ) {
        this.removeMarker();
      }
      this.updateListMarkers(markers);
    });
  }

  public setSelectedMarkerId(id: string | null): void {
    this.updateSelectedMarkerId(id);
  }

  public async cleanup(): Promise<void> {
    const allViewers = [
      ...new Set([
        ...this.previousViewers,
        ...this.previousListViewers,
        ...this.getEligibleViewers(),
      ]),
    ];

    this.applyPreviewMarkerToViewers(null, null, allViewers);
    this.applyMarkersListToViewers([], null, allViewers);

    await Promise.all(
      allViewers
        .map((viewer) => this.iconOperationChains.get(viewer))
        .filter((promise): promise is Promise<void> => promise !== undefined)
    );

    this.disposeRoot?.();
    this.disposeRoot = undefined;
  }

  private getEligibleViewers(): Web3DViewer[] {
    return getEligibleViewers(this.registry, this.isPaneEligible);
  }

  private applyPreviewMarkerToViewers(
    markerPoint: Readonly<Point3D> | null,
    visuals: { markerType?: string; priority?: string } | null,
    viewers: Web3DViewer[]
  ): void {
    for (const viewer of viewers) {
      const previous =
        this.iconOperationChains.get(viewer) ?? Promise.resolve();
      const next = previous
        .then(async () => {
          const iconsPlugin = viewer.plugins.get(IconsPlugin);
          if (!iconsPlugin) return;

          const existing = (iconsPlugin.getIcons() as PointIcon[]).find(
            (icon) => icon.id === PREVIEW_MARKER_ICON_ID
          );
          if (existing) {
            await iconsPlugin.remove(existing);
          }

          if (markerPoint) {
            const iconPath = await getMarkerIconPath({
              markerType: visuals?.markerType,
              priority: visuals?.priority,
              isPending: true,
            });
            const icon: PointIcon = {
              id: PREVIEW_MARKER_ICON_ID,
              iconPath,
              position: new Vector3(
                markerPoint.x,
                markerPoint.y,
                markerPoint.z
              ),
              size: MARKER_ICON_SIZE,
            };
            await iconsPlugin.add(icon);
          }
        })
        .catch((error: unknown) => {
          getLogger().warn('Failed to apply preview marker to viewer', {
            error,
          });
        });
      this.iconOperationChains.set(viewer, next);
    }
  }

  private applyMarkersListToViewers(
    markers: readonly MarkerInfo[],
    selectedId: string | null,
    viewers: Web3DViewer[]
  ): void {
    for (const viewer of viewers) {
      const previous =
        this.iconOperationChains.get(viewer) ?? Promise.resolve();
      const next = previous
        .then(async () => {
          const iconsPlugin = viewer.plugins.get(IconsPlugin);
          if (!iconsPlugin) return;

          const prevIds = this.listIconIds.get(viewer) ?? new Set<number>();
          if (prevIds.size > 0) {
            const allIcons = iconsPlugin.getIcons() as PointIcon[];
            const toRemove = allIcons.filter((icon) => prevIds.has(icon.id));
            for (const icon of toRemove) {
              await iconsPlugin.remove(icon);
            }
          }

          const newIds = new Set<number>();
          const iconsToAdd: PointIcon[] = [];
          const hashMap = new Map<number, string>();

          for (const marker of markers) {
            const hashId = resolveMarkerHashId(marker.issueId, hashMap);
            if (hashId === -1) continue;
            hashMap.set(hashId, marker.issueId);
            const iconPath = await getMarkerIconPath({
              markerType: marker.type,
              priority: marker.priority,
              isSelected: marker.issueId === selectedId,
            });
            newIds.add(hashId);
            iconsToAdd.push({
              id: hashId,
              iconPath,
              position: new Vector3(
                marker.point.x,
                marker.point.y,
                marker.point.z
              ),
              size: MARKER_ICON_SIZE,
            });
          }

          if (iconsToAdd.length > 0) {
            await iconsPlugin.add(iconsToAdd);
          }
          this.listIconIds.set(viewer, newIds);
        })
        .catch((error: unknown) => {
          getLogger().warn('Failed to apply markers list to viewer', { error });
        });
      this.iconOperationChains.set(viewer, next);
    }
  }
}
